import api from "./api";

export async function getCardPayment(id) {
  try {
    const response = await api.get("api/cardpayments/" + id);
    return response.data;
  } catch (error) {
    return [];
  }
}

function hasIdAndSecret(data) {
  return (
    data && typeof data.id === "string" && typeof data.clientData === "string"
  );
}

export async function initCardPayment(parameters) {
  try {
    const res = await api.post("api/cardpayments/init", {
      merchantId: parameters.merchantId,
      amount: parameters.amount,
      cardAccountType: parameters.cardAccountType,
      paymentProvider: parameters.paymentProvider,
    });

    return { data: res.data, error: null, status: res.status ?? 200 };
  } catch (err) {
    // Network / CORS: err.response is *undefined*
    if (!err.response) {
      return { data: null, error: err.message || err, status: null };
    }

    // 4xx / 5xx reached the server – DON'T retry
    return {
      data: null,
      error: err.response.data || err.response.statusText,
      status: err.response.status,
    };
  }
}

export async function finalizeCardPayment(parameters) {
  try {
    const res = await api.post("api/cardpayments/finalize", {
      merchantId: parameters.merchantId,
      paymentId: parameters.paymentId,
      clientData: parameters.clientData,
      paymentProvider: parameters.paymentProvider,
    });

    return res.data;
  } catch (err) {
    if (!err.response) {
      return null; // retry
    }

    return err.response.data ?? { status: err.response.status };
  }
}

// Asks payment-service whether the card is corporate (Adyen's /cardDetails). Resolves
// with { isAmex, isCorporate } or null when the call failed or timed out.
// `context` carries the payment-request id and the drop-in's BIN-lookup result so the
// backend can log lookup-vs-webhook. Undefined fields are omitted from the JSON.
export async function getCardDetails(
  encryptedCardNumber,
  merchantId,
  context = {},
  { timeoutMs } = {}
) {
  try {
    const res = await api.post(
      "api/cardpayments/card-details",
      {
        encryptedCardNumber,
        merchantId,
        paymentRequestId: context.paymentRequestId,
        checkoutId: context.checkoutId,
        paymentMethodVariants: context.paymentMethodVariants,
        detectedBrands: context.detectedBrands,
        issuingCountryCode: context.issuingCountryCode,
      },
      timeoutMs ? { timeout: timeoutMs } : undefined
    );
    return res.data;
  } catch {
    return null;
  }
}

export async function createCardPayment(parameters) {
  try {
    const res = await api.post("api/cardpayments", {
      merchantId: parameters.merchantId,
      paymentRequestId: parameters.paymentRequestId,
      customerId: parameters.customerId,
      orderNumber: parameters.orderNumber,
      checkoutId: parameters.checkoutId,
      customerName: parameters.customerName,
      amount: parameters.amount,
      reference: parameters.reference,
      source: parameters.source,
      clientData: parameters.clientData,
      branchId: parameters.branchId,
      customerEmail: parameters.customerEmail,
      customerIsFinanced: parameters.customerIsFinanced,
      paymentProvider: parameters.paymentProvider,
      ledgerTransactionIds: parameters.transactionIds,
      salesOrderIds: parameters.salesOrderIds,
      idempotencyKey: parameters.idempotencyKey,
      cardAccountType: parameters.cardAccountType ?? "Any",
      discounts: parameters.paymentDiscounts ?? null,
      token: parameters.paymentRequestToken ?? parameters.checkoutToken ?? null,
    });

    if (hasIdAndSecret(res.data)) {
      return res.data; // good payload → stop retrying
    }
    return null; // body missing fields → retry
  } catch (err) {
    // Network / CORS: err.response is *undefined*
    if (!err.response) {
      return null; // retry
    }

    if (err.response.status === 401 && err.response.data?.code === "TokenExpired")
      return { tokenExpired: true };

    // 4xx / 5xx reached the server – DON'T retry
    return err.response.data ?? { status: err.response.status };
  }
}
